// AddFeesSlabModal.tsx
import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

interface AddFeesSlabModalProps {
  visible: boolean;
  courseId: string;
  courseName: string;
  instituteId: string;
  onHide: () => void;
  onSuccess?: () => void;
}

const AddFeesSlabModal: React.FC<AddFeesSlabModalProps> = ({
  visible,
  courseId,
  courseName,
  instituteId,
  onHide,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    base_fee: '',
    minimum_fee: '',
    maximum_fee: '',
    status: 'active'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const baseFee = parseFloat(formData.base_fee);
    const minFee = parseFloat(formData.minimum_fee);
    const maxFee = parseFloat(formData.maximum_fee);

    if (!formData.base_fee || isNaN(baseFee) || baseFee < 0) {
      newErrors.base_fee = 'Valid base fee is required (must be >= 0)';
    }

    if (!formData.minimum_fee || isNaN(minFee) || minFee < 0) {
      newErrors.minimum_fee = 'Valid minimum fee is required (must be >= 0)';
    }

    if (!formData.maximum_fee || isNaN(maxFee) || maxFee < 0) {
      newErrors.maximum_fee = 'Valid maximum fee is required (must be >= 0)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        institute_id: instituteId,
        course_id: courseId,
        base_fee: parseFloat(formData.base_fee),
        minimum_fee: parseFloat(formData.minimum_fee),
        maximum_fee: parseFloat(formData.maximum_fee),
        status: formData.status
      };

      const response = await apiClient.post('/v1/fees-slabs', payload);

      if (response.data.success) {
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(response.data.message || 'Failed to create fees slab');
      }
    } catch (err: any) {
      console.error('Failed to create fees slab:', err);
      const errorMessage = 
        err.response?.data?.error || 
        err.response?.data?.message || 
        err.message || 
        'Failed to create fees slab';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      base_fee: '',
      minimum_fee: '',
      maximum_fee: '',
      status: 'active'
    });
    setErrors({});
    setError(null);
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }
        }
      }}
    >
      <div>
        <div style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '150px',
            height: '150px',
            background: 'rgba(212, 241, 126, 0.1)',
            borderRadius: '50%',
            filter: 'blur(40px)'
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              padding: '4px 10px',
              background: 'rgba(212, 241, 126, 0.2)',
              borderRadius: '16px',
              marginBottom: '8px',
              fontSize: '0.65rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Fees Management
            </div>
            <h2 style={{
              margin: '0 0 6px 0',
              fontSize: '1.4rem',
              fontWeight: '700',
              color: 'white'
            }}>
              Add Fees Slab
            </h2>
            <p style={{
              margin: '0',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.8rem'
            }}>
              Define fee structure for <strong>{courseName}</strong>
            </p>
          </div>
        </div>

        {error && (
          <Message
            severity="error"
            text={error}
            style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
          />
        )}

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="pi pi-book" style={{ color: '#0f3d3e', fontSize: '1.2rem' }}></i>
              <div>
                <p style={{
                  margin: '0 0 2px 0',
                  fontSize: '0.75rem',
                  color: '#6c757d',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Course
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  color: '#0f3d3e',
                  fontWeight: '700'
                }}>
                  {courseName}
                </p>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <GlobalInputField
                label="Minimum Fee (₹)"
                required
                type="number"
                value={formData.minimum_fee}
                onChange={(e) => handleInputChange('minimum_fee', e.target.value)}
                placeholder="e.g., 50000"
                icon="pi pi-arrow-down"
                error={errors.minimum_fee}
              />

              <GlobalInputField
                label="Base Fee (₹)"
                required
                type="number"
                value={formData.base_fee}
                onChange={(e) => handleInputChange('base_fee', e.target.value)}
                placeholder="e.g., 100000"
                icon="pi pi-money-bill"
                error={errors.base_fee}
              />

              <GlobalInputField
                label="Maximum Fee (₹)"
                required
                type="number"
                value={formData.maximum_fee}
                onChange={(e) => handleInputChange('maximum_fee', e.target.value)}
                placeholder="e.g., 150000"
                icon="pi pi-arrow-up"
                error={errors.maximum_fee}
              />
            </div>

            <GlobalDropdownField
              label="Status"
              required
              value={formData.status}
              onChange={(val) => handleInputChange('status', val)}
              options={statusOptions}
              placeholder="Select status"
              icon="pi pi-check-circle"
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <GlobalButton
            label="Cancel"
            variant="outline"
            icon="pi pi-times"
            onClick={handleClose}
            disabled={loading}
          />
          <GlobalButton
            label={loading ? 'Creating Fees Slab...' : 'Create Fees Slab'}
            variant="primary"
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};
//hello
export default AddFeesSlabModal;